import type { ActivePopup } from "../Models/DTOs/ActivePopupDTO";

/**
 * Persists popup dismissals in localStorage, keyed by popupId.
 *
 * A stored value of `null` means a permanent dismissal; a number is the
 * absolute epoch-ms timestamp at which a snoozed popup becomes eligible again.
 */

const KEY_PREFIX = "ickonic_popup_dismiss_";

type Dismissal = { expires: number | null };

const keyFor = (popupId: number) => `${KEY_PREFIX}${popupId}`;

export function dismissPopup(popup: Pick<ActivePopup, "popupId" | "dismissDurationSeconds">): void {
    const ttl = popup.dismissDurationSeconds; // number = snooze seconds, null = forever
    const expires = ttl == null ? null : Date.now() + ttl * 1000;

    try {
        localStorage.setItem(keyFor(popup.popupId), JSON.stringify({ expires } as Dismissal));
    } catch {
        // localStorage unavailable (private mode / quota) — fail silently.
    }
}

export function isDismissed(popupId: number): boolean {
    try {
        const raw = localStorage.getItem(keyFor(popupId));
        if (raw == null) return false; // never dismissed

        const parsed = JSON.parse(raw) as Dismissal;
        if (parsed.expires === null) return true; // permanent dismissal
        if (Date.now() < parsed.expires) return true; // still within snooze window

        // Snooze expired — clean up so it can show again.
        localStorage.removeItem(keyFor(popupId));
        return false;
    } catch {
        return false;
    }
}
